import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LaunchIcon from '@mui/icons-material/Launch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClinicDialog from './ClinicDialog';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import { getClinics, deleteClinic } from './clinicService';

interface Clinic {
  id: number;
  name: string;
  business_id: string;
  practitioner_id: string;
  booking_url_path: string | null;
  logo_url: string | null;
  primary_color: string;
  allowed_appointment_types: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
  timezone: string;
}

const ClinicManager: React.FC = () => {
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState<Clinic | null>(null);
  
  const fetchClinics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getClinics();
      setClinics(data.clinics || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load clinics');
      console.error('Error loading clinics:', err);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchClinics();
  }, []);
  
  const handleAddClick = () => {
    setSelectedClinic(null);
    setOpenDialog(true);
  };
  
  const handleEditClick = (clinic: Clinic) => {
    setSelectedClinic(clinic);
    setOpenDialog(true);
  };
  
  const handleDeleteClick = (clinic: Clinic) => {
    setSelectedClinic(clinic);
    setOpenDeleteDialog(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!selectedClinic) return;
    
    try {
      setLoading(true);
      await deleteClinic(selectedClinic.id);
      setOpenDeleteDialog(false);
      await fetchClinics(); // Refresh the list
    } catch (err: any) {
      setError(err.message || 'Failed to delete clinic');
      console.error('Error deleting clinic:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDialogClose = (refreshNeeded: boolean) => {
    setOpenDialog(false);
    if (refreshNeeded) {
      fetchClinics();
    }
  };
  
  const copyBookingLink = (clinic: Clinic) => {
    const baseUrl = window.location.origin;
    let url;
    
    if (clinic.booking_url_path) {
      url = `${baseUrl}/booking/${clinic.booking_url_path}`;
    } else {
      url = `${baseUrl}/booking?clinic=${clinic.id}`;
    }
    
    navigator.clipboard.writeText(url)
      .then(() => {
        alert('Booking link copied to clipboard!');
      })
      .catch((err) => {
        console.error('Failed to copy booking link:', err);
        alert('Failed to copy booking link. Please copy it manually: ' + url);
      });
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Clinic Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
        >
          Add Clinic
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {loading && !clinics.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Business ID</TableCell>
                <TableCell>Booking URL</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clinics.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No clinics found. Click "Add Clinic" to create one.
                  </TableCell>
                </TableRow>
              ) : (
                clinics.map((clinic) => (
                  <TableRow key={clinic.id}>
                    <TableCell>{clinic.name}</TableCell>
                    <TableCell>{clinic.business_id}</TableCell>
                    <TableCell>
                      {clinic.booking_url_path ? (
                        `/booking/${clinic.booking_url_path}`
                      ) : (
                        `/booking?clinic=${clinic.id}`
                      )}
                      <IconButton
                        size="small"
                        onClick={() => copyBookingLink(clinic)}
                        title="Copy booking link"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={clinic.is_active ? 'Active' : 'Inactive'} 
                        color={clinic.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View booking page">
                        <IconButton
                          size="small"
                          onClick={() => {
                            const baseUrl = window.location.origin;
                            const url = clinic.booking_url_path 
                              ? `${baseUrl}/booking/${clinic.booking_url_path}`
                              : `${baseUrl}/booking?clinic=${clinic.id}`;
                            window.open(url, '_blank');
                          }}
                        >
                          <LaunchIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit clinic">
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(clinic)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete clinic">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(clinic)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {openDialog && (
        <ClinicDialog
          open={openDialog}
          onClose={handleDialogClose}
          clinic={selectedClinic}
        />
      )}
      
      {openDeleteDialog && selectedClinic && (
        <DeleteConfirmationDialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          onConfirm={handleDeleteConfirm}
          clinicName={selectedClinic.name}
        />
      )}
    </Box>
  );
};

export default ClinicManager;